// Authentication utility functions

const TOKEN_EXPIRY_HOURS = 1; // 1 hour expiration
const TOKEN_EXPIRY_MS = TOKEN_EXPIRY_HOURS * 60 * 60 * 1000; // Convert to milliseconds

// Callback for token expiration
let onTokenExpiredCallback = null;

/**
 * Set authentication token with expiration timestamp
 * @param {string} email - User email
 */
export const setAuthToken = (email) => {
  const now = new Date().getTime();
  const expiryTime = now + TOKEN_EXPIRY_MS;

  localStorage.setItem('isAuthenticated', 'true');
  localStorage.setItem('userEmail', email);
  localStorage.setItem('tokenExpiry', expiryTime.toString());
  localStorage.setItem('loginTime', now.toString());

};

/**
 * Check if the current token is valid and not expired
 * @returns {boolean} - True if token is valid and not expired
 */
export const isTokenValid = () => {
  const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
  const tokenExpiry = localStorage.getItem('tokenExpiry');

  if (!isAuthenticated || !tokenExpiry) {
    return false;
  }

  const now = new Date().getTime();
  const expiryTime = parseInt(tokenExpiry, 10);

  if (now > expiryTime) {

    clearAuthToken();

    // Trigger callback if set
    if (onTokenExpiredCallback) {
      onTokenExpiredCallback();
    }

    return false;
  }

  return true;
};

/**
 * Clear all authentication data from localStorage
 */
export const clearAuthToken = () => {
  localStorage.removeItem('isAuthenticated');
  localStorage.removeItem('userEmail');
  localStorage.removeItem('tokenExpiry');
  localStorage.removeItem('loginTime');

};

/**
 * Get remaining time until token expires
 * @returns {number} - Remaining time in milliseconds, or 0 if expired
 */
export const getRemainingTime = () => {
  const tokenExpiry = localStorage.getItem('tokenExpiry');

  if (!tokenExpiry) {
    return 0;
  }

  const now = new Date().getTime();
  const expiryTime = parseInt(tokenExpiry, 10);
  const remaining = expiryTime - now;

  return remaining > 0 ? remaining : 0;
};

/**
 * Get formatted remaining time string
 * @returns {string} - Formatted time string (e.g., "45 minutes")
 */
export const getFormattedRemainingTime = () => {
  const remaining = getRemainingTime();

  if (remaining <= 0) {
    return 'Expired';
  }

  const minutes = Math.floor(remaining / (1000 * 60));
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (hours > 0) {
    return `${hours}h ${remainingMinutes}m`;
  } else {
    return `${remainingMinutes}m`;
  }
};

/**
 * Refresh the token expiry time (extend session)
 */
export const refreshToken = () => {
  if (isTokenValid()) {
    const userEmail = localStorage.getItem('userEmail');
    setAuthToken(userEmail);

    return true;
  }
  return false;
};

/**
 * Set a callback to be called when token expires
 * @param {Function} callback - Function to call on token expiration
 */
export const setOnTokenExpired = (callback) => {
  onTokenExpiredCallback = callback;
};
